import { Box } from '@mui/material'
import { useContext, useEffect, useState } from 'react'

// others
import { useDebounce } from 'use-debounce'
import { ReferencesPayload, useFetchAllReferencesLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY, QUERY_PARAMS } from '../constants'
import { AppContext } from '../context'
import { ActionType } from '../context/AppContextReducer'
import { flexCenter } from '../theme/styleConstants'

// components
import { ContentHead } from '../components/common/ContentHead'
import { EmptyData } from '../components/common/EmptyData'
import { SearchWithKeypress } from '../components/common/SearchWithKeypress'
import AddReference from '../components/referralSources/AddReference'
import ReferencesListing from '../components/referralSources/ReferencesListing'
import { CardBox } from '../theme/styleComponents'
import ReferralTypeDropdown from '../components/referralSources/ReferralTypeDropdown'
import { useSearchParams } from 'react-router-dom'

const ReferralContainer = () => {
  const [referrals, setReferrals] = useState<ReferencesPayload['references']>([])
  const [searchParams] = useSearchParams()
  const { isOpen, searchValue, currentPage, dispatch } = useContext(AppContext)
  const [debouncedSearch] = useDebounce(searchValue, 500)

  const sourceType = searchParams.get(QUERY_PARAMS.SOURCE_TYPE)

  const [fetchAllReferences, { refetch, loading }] = useFetchAllReferencesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError(error) {
      console.error('Error fetching referral sources', error)
      setReferrals([])
    },
    onCompleted(data) {
      const { fetchAllReferences } = data
      const { pagination, references } = fetchAllReferences
      setReferrals(references)
      dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: pagination?.page || 1 })
      dispatch({ type: ActionType.SET_TOTAL_DATA_COUNT, totalDataCount: pagination?.totalCount || 1 })
      dispatch({ type: ActionType.SET_TOTAL_PAGE_COUNT, totalPageCount: pagination?.totalPages || 1 })
    }
  })

  useEffect(() => {
    fetchAllReferences({
      variables: {
        fetchAllReferences: {
          sourceTypeIds: sourceType && sourceType !== QUERY_PARAMS.ALL ? [sourceType] : undefined,
          keyword: debouncedSearch,
          paginationOptions: {
            limit: 20,
            page: currentPage || 1
          }
        }
      }
    })
  }, [fetchAllReferences, currentPage, debouncedSearch, sourceType])

  useEffect(() => {
    return () => {
      dispatch({ type: ActionType.SET_SEARCH_VALUE, searchValue: '' })
      return dispatch({ type: ActionType.SET_CURRENT_PAGE, currentPage: 1 })
    }
  }, [dispatch])

  const isEmpty = !loading && referrals?.length === 0
  const handleClose = () => dispatch({ type: ActionType.SET_IS_OPEN, isOpen: false })

  return (
    <CardBox marginTop={3}>
      <ContentHead title={'Referral Sources'} buttonText={'Add Referral Source'} />

      <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} m={2}>
        <Box display={'flex'} flex={1} justifyContent={'space-between'} alignItems={'center'}>
          <SearchWithKeypress searchPlaceholder={'Search Referral Sources'} />
        </Box>

        <ReferralTypeDropdown />
      </Box>

      {isEmpty ? (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 400px)' }}>
          <EmptyData title={'No Referral Sources!'} />
        </Box>
      ) : (
        <ReferencesListing references={referrals} loading={loading} refetch={refetch} />
      )}
      <AddReference isOpen={isOpen} handleClose={handleClose} />
    </CardBox>
  )
}

export default ReferralContainer
